import { useEffect, useState, useMemo } from 'react'
import { isAdminRole, isParentRole, isTeacherRole } from '@/utils/roles'
import ParentClassCalendarModal from '@/components/parent/ParentClassCalendarModal'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { classesApi, type ClassResponse } from '@/api/endpoints/classes'
import { studentsApi, type StudentResponse } from '@/api/endpoints/students'
import { eventsApi, type ClassEventResponse } from '@/api/endpoints/events'
import { parishesApi } from '@/api/endpoints/parishes'
import { usersApi } from '@/api/endpoints/users'
import { printClassStudentList } from '@/utils/print'
import BirthdayReminders from '@/components/dashboard/BirthdayReminders'
import EditClassModal from '@/components/dashboard/modals/EditClassModal'
import ClassCalendarModal from '@/components/dashboard/modals/ClassCalendarModal'
import ParentContactModal from '@/components/dashboard/modals/ParentContactModal'
import BulkEditModal from '@/components/dashboard/modals/BulkEditModal'
import AppFooter from '@/components/common/AppFooter'
import AppHeader from '@/components/common/AppHeader'
import Spinner from '@/components/common/Spinner'
import { useUI } from '@/stores/ui'

export default function ClassDetailPage() {
  const { classId } = useParams<{ classId: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  // Parents: read-only page. Teachers: may edit only classes they're assigned to (backend enforces too).
  const isParent = isParentRole(user?.role)
  const addToast = useUI((state) => state.addToast)
  
  const [classData, setClassData] = useState<ClassResponse | null>(null)
  const canEdit = !!classData && (isAdminRole(user?.role) ||
    (isTeacherRole(user?.role) && !!user && !!classData.teacher_ids?.includes(user.id)))
  const [students, setStudents] = useState<StudentResponse[]>([])
  const [events, setEvents] = useState<ClassEventResponse[]>([])
  const [parishName, setParishName] = useState('')
  const [lastEditorName, setLastEditorName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [studentFilter, setStudentFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [showEditModal, setShowEditModal] = useState(false)
  const [showCalendarModal, setShowCalendarModal] = useState(false)
  const [showBulkEditModal, setShowBulkEditModal] = useState(false)
  const [studentsExpanded, setStudentsExpanded] = useState(true)
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null)

  useEffect(() => {
    let mounted = true
    const load = async () => {
      if (!classId || !user?.parish_id) return
      setLoading(true)
      try {
        const [classesData, studentsData, eventsData, parishesData] = await Promise.all([
          classesApi.list(),
          studentsApi.list(),
          eventsApi.list(classId),
          parishesApi.list(),
        ])
        
        const cls = Array.isArray(classesData) ? classesData.find(c => c.id === classId) : null
        if (!cls) {
          if (mounted) {
            addToast({ message: 'Class not found', type: 'error' })
            navigate('/dashboard')
          }
          return
        }

        if (mounted) {
          setClassData(cls)
          setStudents(Array.isArray(studentsData) ? studentsData : [])
          setEvents(Array.isArray(eventsData) ? eventsData : [])
          const activeParish = Array.isArray(parishesData) ? parishesData.find(p => p.id === user.parish_id) : null
          if (activeParish) setParishName(activeParish.name)
          
          // Load last editor name if available
          if (cls.last_edited_by_user_id) {
            try {
              const editor = await usersApi.get(cls.last_edited_by_user_id)
              setLastEditorName(editor.first_name && editor.last_name ? `${editor.first_name} ${editor.last_name}` : editor.email)
            } catch {
              // Ignore error
            }
          }
        }
      } catch (error) {
        console.error('Failed to load class details:', error)
        if (mounted) {
          addToast({ message: 'Failed to load class details', type: 'error' })
          navigate('/dashboard')
        }
      } finally {
        if (mounted) setLoading(false)
      }
    }
    load()
    return () => { mounted = false }
  }, [classId, user?.parish_id, navigate, addToast])

  const classStudents = useMemo(() => {
    return students.filter(s => s.class_id === classId)
  }, [students, classId])

  const filteredStudents = useMemo(() => {
    const q = studentFilter.trim().toLowerCase()
    if (!q) return classStudents
    return classStudents.filter(s =>
      `${s.first_name} ${s.last_name}`.toLowerCase().includes(q)
    )
  }, [classStudents, studentFilter])

  const searchResults = useMemo(() => {
    if (!classData || !searchQuery.trim()) return null
    
    const q = searchQuery.trim().toLowerCase()
    const inNotes = classData.notes?.toLowerCase().includes(q) || false
    const matchingEvents = events.filter(ev => {
      const inTopic = ev.topic?.toLowerCase().includes(q)
      const inEventNotes = ev.notes?.toLowerCase().includes(q)
      return inTopic || inEventNotes
    })
    const inEvents = matchingEvents.length > 0
    
    if (inNotes || inEvents) {
      return { 
        found: true, 
        inNotes, 
        inEvents,
        matchingEvents: matchingEvents.map(ev => ev.id)
      }
    }
    return { found: false, matchingEvents: [] }
  }, [classData, events, searchQuery])

  // Helper function to highlight search terms
  const highlightText = (text: string, query: string) => {
    if (!query.trim() || !text) return text
    const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    return parts.map((part, i) => 
      regex.test(part) ? (
        <mark key={i} className="bg-yellow-200 px-0.5 rounded">{part}</mark>
      ) : part
    )
  }

  if (loading || !classData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner label="Loading class details..." />
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      <AppHeader />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <div className="mb-6">
          <Link
            to="/dashboard"
            className="text-sm text-gray-600 hover:text-gray-900 mb-2 inline-block"
          >
            ← Back to Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{classData.name}</h1>
              {classData.class_id && (
                <div className="text-sm text-gray-600 mt-1">
                  Class ID: <span className="font-mono">{classData.class_id}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                onClick={() => setShowCalendarModal(true)}
              >
                📅 Calendar
              </button>
              {canEdit && (<>
              <button
                className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                onClick={() => setShowBulkEditModal(true)}
                title="Add text to many topics or notes at once, or remove notes"
              >
                🔧 Bulk Edit
              </button>
              <button
                className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                onClick={() => setShowEditModal(true)}
                title="Edit this class's name, location, time and notes"
              >
                ✏️ Edit
              </button>
              </>)}
            </div>
          </div>
        </div>

        {/* Class Info */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise mb-6"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="text-sm text-gray-600 mb-1">Location</div>
              <div className="font-medium">{classData.location || classData.address || 'Not specified'}</div>
            </div>
            <div>
              <div className="text-sm text-gray-600 mb-1">Weekly Time</div>
              <div className="font-medium">{classData.weekly_time_desc || classData.weekly_time_description || 'Not specified'}</div>
            </div>
            {classData.start_date && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Start Date</div>
                <div className="font-medium">{new Date(classData.start_date).toLocaleDateString()}</div>
              </div>
            )}
            {lastEditorName && classData.last_edited_at && (
              <div>
                <div className="text-sm text-gray-600 mb-1">Last Edited</div>
                <div className="font-medium">
                  By {lastEditorName} on {new Date(classData.last_edited_at).toLocaleString()}
                </div>
              </div>
            )}
          </div>

          {/* Class Notes */}
          {classData.notes && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-2">Notes</div>
              <div className="text-sm whitespace-pre-wrap">
                {searchQuery.trim() ? highlightText(classData.notes, searchQuery) : classData.notes}
              </div>
            </div>
          )}

          {/* Event Search Results */}
          {searchResults?.inEvents && searchResults.matchingEvents.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="text-sm text-gray-600 mb-2">
                Found in {searchResults.matchingEvents.length} event{searchResults.matchingEvents.length !== 1 ? 's' : ''}:
              </div>
              <div className="space-y-2">
                {events.filter(ev => searchResults.matchingEvents.includes(ev.id)).map(ev => (
                  <div key={ev.id} className="p-2 bg-gray-50 rounded border border-gray-200">
                    <div className="font-medium text-sm">
                      {searchQuery.trim() && ev.topic?.toLowerCase().includes(searchQuery.trim().toLowerCase()) 
                        ? highlightText(ev.topic, searchQuery) 
                        : ev.topic}
                    </div>
                    {ev.notes && searchQuery.trim() && ev.notes.toLowerCase().includes(searchQuery.trim().toLowerCase()) && (
                      <div className="text-xs text-gray-600 mt-1">
                        Notes: {highlightText(ev.notes, searchQuery)}
                      </div>
                    )}
                    {ev.event_date && (
                      <div className="text-xs text-gray-500 mt-1">
                        Date: {new Date(ev.event_date).toLocaleDateString()}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <label className="block text-sm font-medium mb-2">Search Class Topics & Notes</label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="input-field flex-1 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                placeholder="Search for keywords..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              {searchResults?.found && (
                <div className="text-sm text-green-600">
                  ✓ Found in {[
                    searchResults.inNotes && 'notes',
                    searchResults.inEvents && `${searchResults.matchingEvents.length} event${searchResults.matchingEvents.length !== 1 ? 's' : ''}`
                  ].filter(Boolean).join(', ')}
                </div>
              )}
              {searchQuery && searchResults && !searchResults.found && (
                <div className="text-sm text-red-600">No matches found</div>
              )}
            </div>
          </div>
        </motion.div>

        {/* Birthday Reminders */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="mb-6"
        >
          <BirthdayReminders classId={classId} title="Upcoming Birthdays (Next 2 Weeks)" />
        </motion.div>

        {/* Students Assigned */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
        >
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setStudentsExpanded(!studentsExpanded)}
              className="text-lg font-semibold flex items-center gap-2 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 rounded"
            >
              <span>{studentsExpanded ? '▼' : '▶'}</span>
              Children Assigned to Class ({classStudents.length})
            </button>
            {studentsExpanded && (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  className="input-field w-48 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  placeholder="Filter by name..."
                  value={studentFilter}
                  onChange={(e) => setStudentFilter(e.target.value)}
                />
                <button
                  className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  onClick={() => {
                    if (!parishName) {
                      alert('Loading parish data...')
                      return
                    }
                    printClassStudentList({
                      parishName,
                      className: classData.name,
                      teachers: classData.teacher_names || [],
                      students: filteredStudents,
                    })
                  }}
                  title="Print student list"
                >
                  🖨️ Print
                </button>
              </div>
            )}
          </div>

          {studentsExpanded && (
            <div className="mt-4">
              {filteredStudents.length === 0 ? (
                <div className="text-sm text-gray-500 py-4">
                  {studentFilter ? 'No students match your filter' : 'No students assigned to this class'}
                </div>
              ) : (
                <div className="divide-y divide-gray-100 rounded-lg border border-gray-100">
                  {filteredStudents.map(student => (
                    <div key={student.id} className="p-3 flex items-center justify-between hover:bg-gray-50">
                      <div>
                        <div className="font-medium">{student.first_name} {student.last_name}</div>
                        <div className="text-xs text-gray-600">
                          DOB: {student.dob || student.date_of_birth || '—'}
                          {student.allergen_alerts && student.allergen_alerts.length > 0 && (
                            <span className="ml-2 text-red-600">
                              ⚠️ Allergens: {student.allergen_alerts.join(', ')}
                            </span>
                          )}
                        </div>
                      </div>
                      {!isParent && (
                      <button
                        className="btn-secondary text-xs focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                        onClick={() => setSelectedStudentId(student.id)}
                      >
                        View Parent Details
                      </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>

        <EditClassModal
          open={showEditModal}
          onClose={() => setShowEditModal(false)}
          cls={classData}
          onSaved={async (_updated) => {
            // Reload class data
            const classesData = await classesApi.list()
            const cls = Array.isArray(classesData) ? classesData.find(c => c.id === classId) : null
            if (cls) {
              setClassData(cls)
              // Reload editor name
              if (cls.last_edited_by_user_id) {
                try {
                  const editor = await usersApi.get(cls.last_edited_by_user_id)
                  setLastEditorName(editor.first_name && editor.last_name ? `${editor.first_name} ${editor.last_name}` : editor.email)
                } catch {}
              }
            }
          }}
        />

        {isParent ? (
          <ParentClassCalendarModal
            open={showCalendarModal}
            onClose={() => setShowCalendarModal(false)}
            classId={classData.id}
            className={classData.name}
          />
        ) : (
          <ClassCalendarModal
            open={showCalendarModal}
            onClose={() => setShowCalendarModal(false)}
            cls={{ id: classData.id, name: classData.name }}
          />
        )}

        {selectedStudentId && (
          <ParentContactModal
            open={!!selectedStudentId}
            onClose={() => setSelectedStudentId(null)}
            studentId={selectedStudentId}
          />
        )}

        <BulkEditModal
          open={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          classId={classData.id}
          events={events}
          onUpdated={async () => {
            // Reload class data and events
            try {
              const [classesData, eventsData] = await Promise.all([
                classesApi.list(),
                eventsApi.list(classId!),
              ])
              const cls = Array.isArray(classesData) ? classesData.find(c => c.id === classId) : null
              if (cls) {
                setClassData(cls)
                setEvents(Array.isArray(eventsData) ? eventsData : [])
                // Reload editor name if needed
                if (cls.last_edited_by_user_id) {
                  try {
                    const editor = await usersApi.get(cls.last_edited_by_user_id)
                    setLastEditorName(editor.first_name && editor.last_name ? `${editor.first_name} ${editor.last_name}` : editor.email)
                  } catch {}
                }
              }
            } catch (error) {
              console.error('Failed to reload data:', error)
            }
          }}
        />
      </main>
      <AppFooter />
    </div>
  )
}

